/*
 * IndusFact Mascots — Indus & Fact
 * -------------------------------------------------------------------
 * Two doodle-simple letter-form characters representing the two sides
 * of the marketplace and the two initials of the brand.
 *
 *   Indus (the "I")  — the DOER. Yellow hard hat. Appears when a user
 *                      is creating, listing, selling, or celebrating a
 *                      milestone.
 *   Fact  (the "F")  — the CHECKER. Round spectacles. Appears when a
 *                      user is verifying, being moderated, learning,
 *                      or receiving trust signals.
 *
 * Together they spell "IF" — reinforcing brand and motto in one glance:
 *     "No Fikar. Bas IF Kar."
 *
 * Usage
 *   import { Indus, Fact, IndusCheer, FactVerified } from '@/components/mascots/Mascots';
 *   <Indus size={64} label='Post your first listing' />
 *   <FactVerified size={48} />
 *
 * See /app/docs/BRAND_MASCOTS.md for placement rules, motto integration,
 * and phased rollout plan.
 * -------------------------------------------------------------------
 */
import React from 'react';

// Base wrapper for consistent sizing + a11y across all mascot variants.
const MascotBase = ({ size = 96, label, className = '', children, viewBox = '0 0 100 140' }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox={viewBox}
    height={size}
    width={size * (parseFloat(viewBox.split(' ')[2]) / parseFloat(viewBox.split(' ')[3]))}
    fill='none'
    role='img'
    aria-label={label || undefined}
    aria-hidden={label ? undefined : true}
    className={className}
    data-testid='mascot'
  >
    {children}
  </svg>
);

// ---------------------------------------------------------------
// INDUS — default (calm, ready)
// ---------------------------------------------------------------
export const Indus = ({ size = 96, label, className }) => (
  <MascotBase size={size} label={label} className={className}>
    {/* Hard hat */}
    <path d='M 26 24 Q 26 6 50 6 Q 74 6 74 24 Z' fill='#F97316' stroke='#7C2D12' strokeWidth='2.2' strokeLinejoin='round' />
    <rect x='16' y='24' width='68' height='7' rx='2' fill='#F97316' stroke='#7C2D12' strokeWidth='2.2' />
    <line x1='50' y1='6' x2='50' y2='24' stroke='#7C2D12' strokeWidth='1.8' />
    <line x1='30' y1='30' x2='32' y2='38' stroke='#292524' strokeWidth='1.8' strokeLinecap='round' />
    <line x1='70' y1='30' x2='68' y2='38' stroke='#292524' strokeWidth='1.8' strokeLinecap='round' />

    {/* Top bar — head */}
    <rect x='14' y='32' width='72' height='30' rx='5' fill='#FEF6E4' stroke='#292524' strokeWidth='2.6' />
    <circle cx='38' cy='44' r='3' fill='#292524' />
    <circle cx='62' cy='44' r='3' fill='#292524' />
    <circle cx='30' cy='52' r='3.5' fill='#F87171' opacity='0.45' />
    <circle cx='70' cy='52' r='3.5' fill='#F87171' opacity='0.45' />
    <path d='M 42 52 Q 50 59 58 52' stroke='#292524' strokeWidth='2.4' fill='none' strokeLinecap='round' />

    {/* Stem — body */}
    <rect x='38' y='62' width='24' height='50' fill='#FEF6E4' stroke='#292524' strokeWidth='2.6' />
    <path d='M 38 74 Q 30 76 28 82' stroke='#292524' strokeWidth='2.4' fill='none' strokeLinecap='round' />
    <path d='M 62 74 Q 70 76 72 82' stroke='#292524' strokeWidth='2.4' fill='none' strokeLinecap='round' />

    {/* Bottom bar — feet */}
    <rect x='14' y='112' width='72' height='20' rx='5' fill='#FEF6E4' stroke='#292524' strokeWidth='2.6' />
    <line x1='50' y1='112' x2='50' y2='132' stroke='#292524' strokeWidth='2' />
  </MascotBase>
);

// ---------------------------------------------------------------
// INDUS CHEER — celebration / milestone earned
// (used after: first listing approved, subscription purchase, referral converts, streak completed)
// ---------------------------------------------------------------
export const IndusCheer = ({ size = 96, label, className }) => (
  <MascotBase size={size} label={label} className={className} viewBox='0 0 120 140'>
    {/* Hard hat */}
    <path d='M 30 24 Q 30 6 54 6 Q 78 6 78 24 Z' fill='#F97316' stroke='#7C2D12' strokeWidth='2.2' strokeLinejoin='round' />
    <rect x='20' y='24' width='68' height='7' rx='2' fill='#F97316' stroke='#7C2D12' strokeWidth='2.2' />
    <line x1='54' y1='6' x2='54' y2='24' stroke='#7C2D12' strokeWidth='1.8' />
    <line x1='34' y1='30' x2='36' y2='38' stroke='#292524' strokeWidth='1.8' strokeLinecap='round' />
    <line x1='74' y1='30' x2='72' y2='38' stroke='#292524' strokeWidth='1.8' strokeLinecap='round' />

    {/* Top bar — head */}
    <rect x='18' y='32' width='72' height='30' rx='5' fill='#FEF6E4' stroke='#292524' strokeWidth='2.6' />
    {/* Happy squint eyes */}
    <path d='M 38 44 Q 41 40 44 44' stroke='#292524' strokeWidth='2.6' fill='none' strokeLinecap='round' />
    <path d='M 62 44 Q 65 40 68 44' stroke='#292524' strokeWidth='2.6' fill='none' strokeLinecap='round' />
    <circle cx='32' cy='52' r='3.5' fill='#F87171' opacity='0.5' />
    <circle cx='72' cy='52' r='3.5' fill='#F87171' opacity='0.5' />
    <path d='M 44 52 Q 54 62 64 52' stroke='#292524' strokeWidth='2.4' fill='none' strokeLinecap='round' />

    {/* Body */}
    <rect x='42' y='62' width='24' height='50' fill='#FEF6E4' stroke='#292524' strokeWidth='2.6' />
    {/* Left arm — normal */}
    <path d='M 42 74 Q 32 76 30 82' stroke='#292524' strokeWidth='2.4' fill='none' strokeLinecap='round' />
    {/* Right arm — raised */}
    <path d='M 66 68 Q 80 56 92 44' stroke='#292524' strokeWidth='2.6' fill='none' strokeLinecap='round' />
    <circle cx='96' cy='40' r='6' fill='#FEF6E4' stroke='#292524' strokeWidth='2.4' />
    <path d='M 96 34 L 96 28' stroke='#292524' strokeWidth='2.4' strokeLinecap='round' />
    {/* Sparkles */}
    <path d='M 88 20 L 90 24 M 92 22 L 88 22' stroke='#F97316' strokeWidth='2' strokeLinecap='round' />
    <path d='M 108 32 L 110 36 M 112 34 L 108 34' stroke='#F97316' strokeWidth='2' strokeLinecap='round' />

    {/* Feet */}
    <rect x='18' y='112' width='72' height='20' rx='5' fill='#FEF6E4' stroke='#292524' strokeWidth='2.6' />
    <line x1='54' y1='112' x2='54' y2='132' stroke='#292524' strokeWidth='2' />
  </MascotBase>
);

// ---------------------------------------------------------------
// FACT — default (polite, ready to help)
// ---------------------------------------------------------------
export const Fact = ({ size = 96, label, className }) => (
  <MascotBase size={size} label={label} className={className}>
    {/* Stem */}
    <rect x='14' y='8' width='32' height='120' rx='5' fill='#FEF6E4' stroke='#1E3A5F' strokeWidth='2.6' />
    {/* Top bar */}
    <rect x='46' y='8' width='42' height='24' rx='5' fill='#FEF6E4' stroke='#1E3A5F' strokeWidth='2.6' />
    {/* Middle bar */}
    <rect x='46' y='50' width='30' height='18' rx='5' fill='#FEF6E4' stroke='#1E3A5F' strokeWidth='2.6' />

    {/* Face + spectacles */}
    <circle cx='22' cy='24' r='2.5' fill='#292524' />
    <circle cx='38' cy='24' r='2.5' fill='#292524' />
    <circle cx='22' cy='24' r='7' fill='none' stroke='#1E3A5F' strokeWidth='2.6' />
    <circle cx='38' cy='24' r='7' fill='none' stroke='#1E3A5F' strokeWidth='2.6' />
    <line x1='29' y1='24' x2='31' y2='24' stroke='#1E3A5F' strokeWidth='2.6' />
    <circle cx='17' cy='34' r='2.8' fill='#F87171' opacity='0.4' />
    <circle cx='43' cy='34' r='2.8' fill='#F87171' opacity='0.4' />
    <path d='M 22 40 Q 30 45 38 40' stroke='#292524' strokeWidth='2.2' fill='none' strokeLinecap='round' />

    {/* Pen sticking out of pocket on middle bar */}
    <line x1='60' y1='46' x2='60' y2='56' stroke='#1E3A5F' strokeWidth='2.2' strokeLinecap='round' />
    <circle cx='60' cy='46' r='1.5' fill='#F97316' />

    {/* Feet */}
    <ellipse cx='22' cy='130' rx='7' ry='3' fill='#292524' />
    <ellipse cx='38' cy='130' rx='7' ry='3' fill='#292524' />
  </MascotBase>
);

// ---------------------------------------------------------------
// FACT VERIFIED — moderation approval / GST verified
// (used at: listing approved, GST badge granted, moderator sign-off)
// ---------------------------------------------------------------
export const FactVerified = ({ size = 96, label, className }) => (
  <MascotBase size={size} label={label} className={className} viewBox='0 0 110 140'>
    <rect x='14' y='8' width='32' height='120' rx='5' fill='#FEF6E4' stroke='#1E3A5F' strokeWidth='2.6' />
    <rect x='46' y='8' width='42' height='24' rx='5' fill='#FEF6E4' stroke='#1E3A5F' strokeWidth='2.6' />
    <rect x='46' y='50' width='30' height='18' rx='5' fill='#FEF6E4' stroke='#1E3A5F' strokeWidth='2.6' />

    {/* Happy squint eyes */}
    <path d='M 18 24 Q 22 20 26 24' stroke='#292524' strokeWidth='2.4' fill='none' strokeLinecap='round' />
    <path d='M 34 24 Q 38 20 42 24' stroke='#292524' strokeWidth='2.4' fill='none' strokeLinecap='round' />
    <circle cx='22' cy='24' r='7' fill='none' stroke='#1E3A5F' strokeWidth='2.6' />
    <circle cx='38' cy='24' r='7' fill='none' stroke='#1E3A5F' strokeWidth='2.6' />
    <line x1='29' y1='24' x2='31' y2='24' stroke='#1E3A5F' strokeWidth='2.6' />
    <circle cx='17' cy='34' r='2.8' fill='#F87171' opacity='0.5' />
    <circle cx='43' cy='34' r='2.8' fill='#F87171' opacity='0.5' />
    <path d='M 22 39 Q 30 46 38 39' stroke='#292524' strokeWidth='2.4' fill='none' strokeLinecap='round' />

    {/* Clipboard with big green check */}
    <rect x='54' y='12' width='30' height='18' rx='2' fill='#FFFFFF' stroke='#1E3A5F' strokeWidth='2' />
    <rect x='63' y='9' width='12' height='4' rx='1' fill='#1E3A5F' />
    <path d='M 59 22 L 65 26 L 78 15' stroke='#16A34A' strokeWidth='3.5' fill='none' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M 88 8 L 92 14 M 92 8 L 88 14' stroke='#16A34A' strokeWidth='2' strokeLinecap='round' />

    {/* Feet */}
    <ellipse cx='22' cy='130' rx='7' ry='3' fill='#292524' />
    <ellipse cx='38' cy='130' rx='7' ry='3' fill='#292524' />
  </MascotBase>
);

// ---------------------------------------------------------------
// The pair — Indus + Fact side-by-side. Use in onboarding step 3
// ("Get inquiries — you're set"), the /whats-new hero, and the
// footer motto tag.
// ---------------------------------------------------------------
export const IndusAndFact = ({ size = 96, label, className = '' }) => (
  <div className={`inline-flex items-end gap-1 ${className}`} role='img' aria-label={label || 'Indus and Fact — IndusFact mascots'} data-testid='mascot-pair'>
    <Indus size={size} />
    <Fact size={size} />
  </div>
);

// Default export for convenience.
const Mascots = { Indus, Fact, IndusCheer, FactVerified, IndusAndFact };
export default Mascots;
